import { useState, useEffect } from 'react';
import { supabaseGetter } from '~/api/supabase-getter';

/**
 * Hook to fetch propagation codes for a crop template
 * @param {Object} params
 * @param {string} params.templateId - Template UUID
 * @returns {Object} Propagation codes array, loading state, and error
 */
export const useCropTemplatePropagation = ({ templateId }) => {
  const [propagationCodes, setPropagationCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Early return if no template selected
    if (!templateId) {
      setPropagationCodes([]);
      setLoading(false);
      setError(null);
      return;
    }

    async function fetchData() {
      setLoading(true);
      setError(null);

      try {
        const { data: propagationData, error: propagationError } =
          await supabaseGetter.cropTemplatePropagation.getByTemplate(templateId);

        if (propagationError) {
          console.warn('[useCropTemplatePropagation] Error fetching propagation codes - table may not exist yet:', propagationError);
          // Don't throw - just return empty array (will use default 'indoor_sow' in UI)
          setPropagationCodes([]);
          setError(null);  // Don't treat as error if table doesn't exist
          setLoading(false);
          return;
        }

        // Extract just the codes from the array of objects
        const codes = propagationData?.map(p => p.propagation_code) || [];
        setPropagationCodes(codes);
      } catch (err) {
        console.error('[useCropTemplatePropagation] Unexpected error:', err);
        setError(err.message);
        setPropagationCodes([]);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [templateId]);

  return {
    propagationCodes,
    loading,
    error,
    invalidate: () => {
      supabaseGetter.cropTemplatePropagation.invalidate();
    }
  };
};
